/**
 * 組織の噛み合わせ（コミュニケーション負荷）— 説明文DB DB03。
 *
 * 正典: apps/web/docs/新方針20260821/GB組織_説明文DB_v1_23.json の DB03 / DB12
 *
 * 【これは何を見ているか】
 *  つながりの構造（2章）が「実際に誰に相談したか」の記録であるのに対し、
 *  ここは「そもそも話が噛み合いやすい顔ぶれか」を4軸の分かれ方から見る。
 *  相談記録には表れない、コミュニケーションの取りにくさを補う位置づけ。
 *
 * 【分極の判定は組織タイプ(1-2)と同じものを使う（2026-08-22 変更）】
 *  分極率の定義はDB内に3つある。
 *   ・仕様書§04 / DB14 … 対角ペアで優勢な人を数える。人数別の帰無基準と比較
 *   ・DB12          … 対角スコアを中央値で中心化し、±4点以上離れた両側の少ないほうを2倍
 *   ・herrmannLoad  … 4 × pA × pC（旧実装）
 *  当初は DB03 の判定入力の記述に従い DB12 で判定していたが、総当たり検査で
 *  「組織タイプ＝二極型」なのに「噛み合わせ＝分極が低い」が同じレポートに並ぶ
 *  ケースが見つかった（3軸優勢の組織で、対角の差が4点に届かないとき）。
 *  読み手には矛盾に見えるため、レポート内の分極は1つの判定に統一し、
 *  組織タイプの「二極型」判定をそのまま使う。
 *  DB12 の値は比較・検算用に残す（polarRateDB12 / polarHighDB12）。
 *
 * 【橋渡し・緩衝・脆さ】
 *  DB03 は厚/薄・高/低としか書いていないため、既存の herrmannLoad.ts の定義と
 *  閾値をそのまま使う（橋渡し25%以上で厚い、緩衝65%/35%、脆さ50%/25%）。
 */
import type { AxisKey } from './gb74Report';
import { computeHerrmannLoad, BRIDGE_HI, type HerrmannLoadMember } from './herrmannLoad';
import { findOrgText, type OrgTextEntry } from './orgTextsV3';

/** 対角ペア。 */
export type DiagonalKey = 'AC' | 'BD';

/** 対角スコアの差がこれ以上離れていれば「片側に寄っている」とみなす（DB12 12-定義）。 */
export const POLAR_GAP = 4;

/** 分極を判定できる最小人数（DB12 12-基準表）。 */
export const POLAR_MIN_MEMBERS = 15;

/** 緩衝・脆さの修飾句の閾値（DB03 03-MOD-*）。 */
export const BUFFER_HI = 0.65;
export const BUFFER_LO = 0.35;
export const FRAGILE_HI = 0.5;
export const FRAGILE_LO = 0.25;

/**
 * 分極が「高い」とみなす閾値（DB12 12-基準表）。
 * 15名未満は判定しない。15〜25名は55%以上、26名以上は50%以上。
 *
 * 固定40%案が否定されている点に注意。偶然でも分極率の中央値が33〜38%出るため、
 * 40%では n=22 で4社に1社が偶然で該当してしまう。
 */
export function polarHighThreshold(n: number): number {
  if (n < POLAR_MIN_MEMBERS) return Infinity;
  return n <= 25 ? 0.55 : 0.5;
}

/**
 * DB12 の分極率。
 *  ① 各社員の対角スコア p（A↔Cなら A − C、範囲 −12〜+12）
 *  ② 中央値を引いて中心化 q = p − median(p)。組織全体の傾きを除く
 *  ③ しきい差 t = 4点（ハーマン1ペア分＝意味のある差の最小単位）
 *  ④ 分極率 ＝ 2 × min(q≧+t の人数, q≦−t の人数) ÷ 回答者数
 */
export function polarizationRateDB12(
  members: Array<Record<AxisKey, number>>,
  diag: DiagonalKey
): number {
  const n = members.length;
  if (n === 0) return 0;
  const [x, y] = diag === 'AC' ? (['A', 'C'] as const) : (['B', 'D'] as const);
  const p = members.map((m) => m[x] - m[y]);
  const sorted = [...p].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  const q = p.map((v) => v - median);
  const hi = q.filter((v) => v >= POLAR_GAP).length;
  const lo = q.filter((v) => v <= -POLAR_GAP).length;
  return (2 * Math.min(hi, lo)) / n;
}

export interface OrgCohesionV3 {
  /** 分極率（DB12）。AC と BD の大きいほう */
  polarRate: number;
  /** 大きいほうの対角ペア */
  diagonal: DiagonalKey;
  polarAC: number;
  polarBD: number;
  /**
   * 分極が高いか。組織タイプの「二極型」判定をそのまま使う。
   * 型が判定できない人数のときは null（判定不可）。
   */
  polarHigh: boolean | null;
  /** DB12 の定義で計算した分極率（比較・検算用。判定には使わない） */
  polarRateDB12: number;
  /** DB12 の閾値で見たときの高低（比較用） */
  polarHighDB12: boolean | null;
  /** 橋渡し在庫（0〜1）と厚いかどうか */
  bridge: number;
  bridgeThick: boolean;
  /** 緩衝力・脆さ（0〜1） */
  buffer: number;
  fragility: number;
  /** DB03 の本体文言 */
  text?: OrgTextEntry;
  /** DB03 の修飾句（緩衝・脆さ） */
  modifiers: OrgTextEntry[];
  memberCount: number;
}

/** 4軸と緩衝力の素から、噛み合わせを判定して文言を引く。 */
export function buildOrgCohesionV3(
  members: Array<{ axis: Record<AxisKey, number>; buffer?: number | null }>,
  /**
   * 組織タイプの判定結果。'二極型' なら分極が高いとみなす。
   * null は人数が足りず型を判定できない場合。
   */
  orgShape?: string | null
): OrgCohesionV3 {
  const n = members.length;
  const axes = members.map((m) => m.axis);
  const polarAC = polarizationRateDB12(axes, 'AC');
  const polarBD = polarizationRateDB12(axes, 'BD');
  const diagonal: DiagonalKey = polarBD > polarAC ? 'BD' : 'AC';
  const polarRate = Math.max(polarAC, polarBD);

  const load = computeHerrmannLoad(
    members.map((m) => ({ axis: m.axis, buffer: m.buffer ?? null }) as HerrmannLoadMember)
  );
  const bridgeThick = load.bridge >= BRIDGE_HI;

  // DB12 の判定（比較・検算用に保持する）
  const polarHighDB12 = n < POLAR_MIN_MEMBERS ? null : polarRate >= polarHighThreshold(n);
  // 実際に使うのは組織タイプの判定。レポート内で分極の結論を1つにするため。
  const polarHigh =
    orgShape === undefined ? polarHighDB12 : orgShape === null ? null : orgShape === '二極型';

  const modifiers: OrgTextEntry[] = [];
  if (load.buffer >= BUFFER_HI) push(modifiers, findOrgText('DB03', '03-MOD-緩衝高'));
  else if (load.buffer < BUFFER_LO) push(modifiers, findOrgText('DB03', '03-MOD-緩衝低'));
  if (load.fragility >= FRAGILE_HI) push(modifiers, findOrgText('DB03', '03-MOD-脆さ高'));
  else if (load.fragility < FRAGILE_LO) push(modifiers, findOrgText('DB03', '03-MOD-脆さ低'));

  const text =
    polarHigh === null
      ? findOrgText('DB03', '03-MOD-判定不可')
      : findOrgText(
          'DB03',
          `03-${polarHigh ? '高' : '低'}${bridgeThick ? '厚' : '薄'}-${diagonal}`
        );

  return {
    polarRate,
    diagonal,
    polarAC,
    polarBD,
    polarHigh,
    polarRateDB12: polarRate,
    polarHighDB12,
    bridge: load.bridge,
    bridgeThick,
    buffer: load.buffer,
    fragility: load.fragility,
    text,
    modifiers,
    memberCount: n,
  };
}

function push(list: OrgTextEntry[], e?: OrgTextEntry) {
  if (e) list.push(e);
}
